/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useRef, useEffect } from 'react';
import { jsx, css } from '@emotion/react';
import { EPickerTheme, ETypography, ETypographyColor, Icons } from '../../../types';
import { useColors } from '../../../hooks/useColors';
import { Typography } from '../../../atoms';
import { Button } from '../../../components/Button';
import { BnIcon } from '../../../components/icons_v2';
import { gapCss, flexCss } from '../../../styles/utils';
import { SizeEnum } from '../../../lib/constants';
import { Tooltip } from '../../../components/Tooltip';

const alignItemsCenterCss = css`
	align-items: center;
`;

interface CopyInvoiceLinkButtonLabels {
	copyInvoiceLinkDescription: string;
	linkCopied: string;
	copyInvoice: string;
	copyInvoiceLink: string;
}

interface CopyInvoiceLinkButtonProps {
	labels: CopyInvoiceLinkButtonLabels;
	invoiceId: number;
	organizationId: number;
	userId: number;
}

/**
 * Legacy fallback for copying text when Clipboard API is unavailable
 * (non-HTTPS, non-localhost, or unsupported browsers)
 */
const copyWithLegacyFallback = (text: string): boolean => {
	const el = document.createElement('textarea');
	el.value = text;

	document.body.appendChild(el);
	el.select();
	const success = document.execCommand('copy');
	document.body.removeChild(el);
	return success;
};

export const CopyInvoiceLinkButton = ({ labels, invoiceId, organizationId, userId }: CopyInvoiceLinkButtonProps) => {
	const [hasCopiedLink, setHasCopiedLink] = useState(false);
	const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { colors } = useColors();

	const onCopySuccess = () => {
		if (copyResetTimeoutRef.current) {
			clearTimeout(copyResetTimeoutRef.current);
		}

		setHasCopiedLink(true);

		copyResetTimeoutRef.current = setTimeout(() => {
			setHasCopiedLink(false);
		}, 2000);
	};

	const getInvoiceLink = () => {
		const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
		return `${baseUrl}/invoice/${invoiceId}?o=${organizationId}&u=${userId}`;
	};

	const copyInvoiceLink = async () => {
		const link = getInvoiceLink();
		if (window.navigator?.clipboard?.writeText) {
			try {
				await window.navigator.clipboard.writeText(link);
				onCopySuccess();
			} catch {
				if (copyWithLegacyFallback(link)) {
					onCopySuccess();
				}
			}
		} else {
			if (copyWithLegacyFallback(link)) {
				onCopySuccess();
			}
		}
	};

	useEffect(() => {
		return () => {
			if (copyResetTimeoutRef.current) {
				clearTimeout(copyResetTimeoutRef.current);
			}
		};
	}, []);

	return (
		<Tooltip content={labels.copyInvoiceLinkDescription} placement="top-center">
			<Button
				data-aid="button-checkoutCardButton-copy"
				sizer={SizeEnum.MEDIUM}
				theme={EPickerTheme.BASIC}
				aria-label={hasCopiedLink ? labels.linkCopied : labels.copyInvoice}
				onClick={copyInvoiceLink}
			>
				<div css={[flexCss, gapCss(8), alignItemsCenterCss]}>
					<BnIcon
						icon={hasCopiedLink ? Icons.check : Icons.copy}
						color={hasCopiedLink ? colors.t_green : colors.tx_text_primary}
					/>
					<Typography type={ETypography.body1} color={ETypographyColor.primary}>
						{labels.copyInvoiceLink}
					</Typography>
				</div>
			</Button>
		</Tooltip>
	);
};

